<script lang="ts">
  import { _, number } from 'svelte-i18n';

  import { logger } from '$lib';
  import type { CantonCount } from '$lib/api/listprofile';

  /**
   * Where a customer's companies are, on the shape of the country.
   *
   * # Why a map rather than the list of cantons beside it
   *
   * The bar list answers "which canton has most", which the customer could read
   * from a table. The map answers the question they actually arrived with —
   * "is this my patch?" — and it answers it before anything is read, because a
   * Swiss person knows the shape of Switzerland and can see a hole in it.
   *
   * # It draws the spread, not the standouts
   *
   * `region.cantons` is the thresholded list: the cantons where the customer is
   * unusual. For most customers it is empty, because most cover their language
   * region evenly. A map drawn from it would be blank for the ordinary case,
   * which is the opposite of what a map is for. `region.spread` is every canton
   * with a count, and that is what is painted.
   */

  interface Props {
    spread: CantonCount[];

    /** Drawn a little larger where there is room for it. */
    compact?: boolean;
  }

  let { spread, compact = false }: Props = $props();

  /**
   * The outlines, keyed by the abbreviation the API returns.
   *
   * Four deep: a canton is a list of polygons — Ticino and Fribourg have
   * exclaves — each polygon a list of rings, each ring a list of `[lon, lat]`.
   * The first ring of a polygon is its outline and any others are holes, which
   * matters for Bern, wrapped entirely around Solothurn's.
   */
  type Point = [number, number];
  type Ring = Point[];
  type Polygon = Ring[];
  type Shapes = Record<string, Polygon[]>;

  let shapes = $state<Shapes | null>(null);

  /**
   * The outlines are fetched, not imported.
   *
   * 56KB of geometry in the main bundle is paid for by every customer, and only
   * the ones who upload a list ever see it. Same origin, so the strict CSP that
   * forbids a third-party host is satisfied — which is also why the file is
   * carried in `static/` rather than pulled from a CDN.
   */
  $effect(() => {
    if (shapes) return;
    fetch('/cantons.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: Shapes) => (shapes = data))
      // A missing map is not worth an error on screen: the same numbers are in
      // the list beside it, so the panel simply renders without the picture.
      .catch((error) => logger.warn('swiss map: outlines unavailable', error));
  });

  const width = 200;
  const height = 132;
  const pad = 4;

  const counts = $derived(new Map(spread.map((c) => [c.canton, c.companies])));
  const most = $derived(Math.max(1, ...spread.map((c) => c.companies)));
  const total = $derived(spread.reduce((sum, c) => sum + c.companies, 0));

  /**
   * Equirectangular, longitude squeezed by cos(latitude), one scale for both
   * axes. Switzerland is small enough that anything projected more carefully
   * would not differ by a pixel at this size.
   */
  function project(shapes: Shapes) {
    let minLon = 180;
    let maxLon = -180;
    let minLat = 90;
    let maxLat = -90;

    for (const polys of Object.values(shapes)) {
      for (const polygon of polys) {
        for (const [lon, lat] of polygon[0]) {
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
        }
      }
    }

    const k = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180);
    const [x0, x1] = [minLon * k, maxLon * k];
    const [y0, y1] = [-maxLat, -minLat];
    const scale = Math.min((width - 2 * pad) / (x1 - x0), (height - 2 * pad) / (y1 - y0));
    const offX = (width - (x1 - x0) * scale) / 2;
    const offY = (height - (y1 - y0) * scale) / 2;

    return {
      x: (lon: number) => offX + (lon * k - x0) * scale,
      y: (lat: number) => offY + (-lat - y0) * scale
    };
  }

  // Every ring of every polygon, so exclaves are drawn and holes are punched:
  // with `fill-rule` at its default, a ring inside another subtracts from it.
  function pathFor(polys: Polygon[], p: ReturnType<typeof project>): string {
    return polys
      .map((polygon) =>
        polygon
          .map(
            (ring) =>
              'M' +
              ring.map(([lon, lat]) => `${p.x(lon).toFixed(1)},${p.y(lat).toFixed(1)}`).join('L') +
              'Z'
          )
          .join('')
      )
      .join('');
  }

  /**
   * How strongly a canton is painted.
   *
   * # Square root, not linear
   *
   * Zürich holds several times what Uri does, and on a linear ramp everything
   * but the largest one or two cantons washes out to nothing. The root spreads
   * the middle of the range across the middle of the scale, which is where all
   * but a handful of cantons sit.
   *
   * # And a deliberately short ramp
   *
   * It stops at 0.5 rather than running to solid. This is a small panel inside a
   * screen whose accent colour already means "you have chosen this" — the
   * checkboxes, the buttons. A map painted to full strength competes with the
   * controls and reads as the loudest thing on the page, which it is not: it is
   * context for a decision made elsewhere. A wash is enough to see the shape of
   * where somebody sells.
   */
  function weight(count: number): number {
    if (count <= 0) return 0;
    return 0.12 + 0.38 * Math.sqrt(count / most);
  }

  /**
   * A canton with nobody in it is drawn, in grey.
   *
   * Left at a transparent accent it renders as nothing, and the country loses
   * the parts the customer does not sell to — which for a Zürich-only customer
   * is most of it. The silhouette has to stay whole for the filled parts to
   * mean anything: "we have no customers here" and "this is not on the map"
   * cannot look the same.
   *
   * Grey rather than the palest accent step, so presence and absence differ in
   * hue and not only in strength. The lightest accent against white is under
   * 3:1, which is exactly the case a reader cannot resolve.
   */
  function fillFor(count: number): string {
    return count > 0 ? `rgb(124 58 237 / ${weight(count).toFixed(3)})` : 'rgb(243 244 246)';
  }

  const rendered = $derived.by(() => {
    // Bound to a local: the null check does not survive into the callbacks
    // below, and the alternative is a non-null assertion at four call sites.
    const outlines = shapes;
    if (!outlines) return null;
    const p = project(outlines);

    const painted = Object.entries(outlines).map(([canton, polys]) => ({
      canton,
      d: pathFor(polys, p),
      count: counts.get(canton) ?? 0
    }));

    // Only the largest few are labelled. Every canton labelled is a pile of
    // overlapping numbers across the north-west, where the small ones crowd.
    const labels = [...spread]
      .slice(0, compact ? 3 : 5)
      .filter((c) => outlines[c.canton] && c.companies > 0)
      .map((c) => {
        // The largest ring's average sits inside the canton, where a
        // bounding-box centre does not for Valais or Graubünden — and taking
        // the largest ring rather than the first keeps the label off an
        // exclave.
        let biggest: Ring = outlines[c.canton][0][0];
        for (const polygon of outlines[c.canton]) {
          if (polygon[0].length > biggest.length) biggest = polygon[0];
        }
        const lon = biggest.reduce((a, pt) => a + pt[0], 0) / biggest.length;
        const lat = biggest.reduce((a, pt) => a + pt[1], 0) / biggest.length;
        return { canton: c.canton, companies: c.companies, x: p.x(lon), y: p.y(lat) };
      });

    return { painted, labels };
  });
</script>

{#if rendered}
  <figure class="m-0">
    <svg
      viewBox="0 0 {width} {height}"
      class="w-full"
      role="img"
      aria-label={$_('guided.listProfile.map.alt', {
        values: { count: $number(total), cantons: spread.length }
      })}
    >
      {#each rendered.painted as area (area.canton)}
        <!--
          A hairline between neighbours, in the surface colour. Two adjacent
          cantons at similar strength otherwise merge into one blob and the
          country loses its internal shape.
        -->
        <path
          d={area.d}
          fill={fillFor(area.count)}
          stroke="white"
          stroke-width="0.4"
          stroke-linejoin="round"
        >
          <title>{area.canton}: {$number(area.count)}</title>
        </path>
      {/each}

      {#each rendered.labels as label (label.canton)}
        <text
          x={label.x.toFixed(1)}
          y={label.y.toFixed(1)}
          text-anchor="middle"
          class="pointer-events-none"
        >
          <tspan class="fill-gray-700 text-[5px] font-semibold">{label.canton}</tspan>
          <tspan x={label.x.toFixed(1)} dy="4.5" class="fill-gray-500 text-[4px] tabular-nums">
            {$number(label.companies)}
          </tspan>
        </text>
      {/each}
    </svg>

    <!--
      Attribution, because the outlines are swisstopo boundaries by way of the
      swiss-maps package under BSD-3-Clause, and the licence asks for it on any
      page that draws them.
    -->
    <!--
      One line, and it has to stay one: the label sits under a 224px map, and
      the fuller wording ran 259px and wrapped. Measured rather than guessed —
      this fits with about 30px to spare, so `nowrap` guarantees the line
      without shrinking the type to something nobody can read.
    -->
    <figcaption class="mt-1 truncate whitespace-nowrap text-[10px] text-gray-400">
      {$_('guided.listProfile.map.credit')}
    </figcaption>
  </figure>
{/if}
